/**
 * `RemoteConfig` Zod — fonte central de business values (§4.17, ADR-0069).
 *
 * Categoria por sub-objeto: pricing, refund, trial, limits, retention,
 * intervals, ai, marketplace. `configVersion: literal(1)` permite migração
 * versionada futura (bump = mismatch claro, não silencioso).
 *
 * `DEFAULT_CONFIG` aceita runtime safe quando server indisponível na
 * primeira execução (sem cache local).
 */
import { z } from 'zod';
import { capabilityMatrixSchema, DEFAULT_CAPABILITY_MATRIX } from '../capabilities.js';

export const PricingTierSchema = z.enum(['free', 'per_template', 'pro', 'team', 'enterprise']);
export type PricingTier = z.infer<typeof PricingTierSchema>;

/* Preço positivo + finito previne `R$ NaN` na UI e admin malicioso
   cobrar valor negativo via config envenenado. `generatedAt` positivo
   garante comparação "config mais novo que cache" funcione. */
const moneyAmount = z.number().nonnegative().finite();

const scannerSeveritySchema = z.enum(['error', 'warning', 'info']);
const scannerPolicySchema = z.object({
  enabled: z.boolean(),
  maxFindings: z.number().int().positive(),
  maxArchiveBytes: z.number().int().positive(),
  timeoutMs: z.number().int().positive(),
  scanTimeoutMs: z.number().int().positive(),
  dataPayloadMinBase64: z.number().int().positive(),
  maxVendoredFiles: z.number().int().nonnegative(),
  failOn: z.array(scannerSeveritySchema),
  rules: z.array(
    z.object({
      id: z.string().min(1),
      severity: scannerSeveritySchema,
      enabled: z.boolean(),
    }),
  ),
});
type ScannerPolicy = z.infer<typeof scannerPolicySchema>;

/** Regras que preservam a capacidade do scanner de analisar o código. Elas não podem ser
 *  desligadas, rebaixadas nem excluídas pela configuração remota. */
export const REQUIRED_SCANNER_RULES = [
  { id: 'eval-call', severity: 'error' },
  { id: 'new-function', severity: 'error' },
  { id: 'dynamic-code-reference', severity: 'error' },
  { id: 'process-builtin-loader', severity: 'error' },
  { id: 'process-native-escape', severity: 'error' },
  { id: 'vm-code-loading', severity: 'error' },
  { id: 'dynamic-require', severity: 'error' },
  { id: 'dynamic-import', severity: 'error' },
  { id: 'obfuscation-suspect', severity: 'error' },
] as const;

export function isRequiredScannerRuleId(id: string): boolean {
  return REQUIRED_SCANNER_RULES.some((rule) => rule.id === id);
}

/** Canonicaliza snapshots antigos e writes do Admin sem invalidar todo o RemoteConfig. */
export function enforceRequiredScannerPolicy(scanner: ScannerPolicy): ScannerPolicy {
  const configuredRuleIds = new Set(scanner.rules.map((rule) => rule.id));
  return {
    ...scanner,
    failOn: scanner.failOn.includes('error') ? scanner.failOn : ['error', ...scanner.failOn],
    rules: [
      ...scanner.rules.map((rule) =>
        isRequiredScannerRuleId(rule.id)
          ? { ...rule, severity: 'error' as const, enabled: true }
          : rule,
      ),
      ...REQUIRED_SCANNER_RULES.filter((rule) => !configuredRuleIds.has(rule.id)).map((rule) => ({
        ...rule,
        enabled: true,
      })),
    ],
  };
}

export const RemoteConfigSchema = z.object({
  configVersion: z.literal(1),
  /* `generatedAt = 0` é sentinel para `DEFAULT_CONFIG` (config nunca
     gerado por server). Valores reais sempre positivos. */
  generatedAt: z.number().int().nonnegative().finite(),

  pricing: z.object({
    pro: z.object({
      monthlyBrl: moneyAmount,
      annualBrl: moneyAmount,
      currency: z.enum(['BRL', 'USD']),
    }),
    team: z.object({ monthlyBrl: moneyAmount }),
    perTemplate: z.object({
      monthlyBrl: moneyAmount,
      annualBrl: moneyAmount,
    }),
  }),

  refund: z.object({
    annualDays: z.number().int().positive(),
    monthlyDays: z.number().int().nonnegative(),
    oneTimeDays: z.number().int().positive(),
  }),

  trial: z.object({
    premiumPackDays: z.number().int().positive(),
    requiresPaymentMethod: z.boolean(),
  }),

  limits: z.object({
    recentProjects: z.number().int().positive(),
    packSizeMb: z.number().int().positive(),
    historyDays: z.number().int().nonnegative(),
    chatSessionsPerProject: z.number().int().positive(),
  }),

  retention: z.object({
    logsDays: z.number().int().positive(),
    backupsDays: z.number().int().positive(),
    debugBundleMaxMb: z.number().int().positive(),
    /* Telemetria operacional bruta do licensing. Default no schema mantém snapshots
       persistidos antes desta chave retrocompatíveis; produção pode rotacionar a
       política pelo config-service sem novo deploy. */
    deviceHeartbeatsDays: z.number().int().positive().default(30),
  }),

  intervals: z.object({
    licenseRefreshMs: z.number().int().positive(),
    configRefreshMs: z.number().int().positive(),
    fileWatcherDebounceMs: z.number().int().positive(),
    autoSaveMs: z.number().int().positive(),
    /* Cadência da varredura do prune de histórico de sessões (ADR-0604). Per-plano
       é a RETENÇÃO em dias (capability `history.retention`); a CADÊNCIA de varredura
       é global e barata (retenção é de dias, não de minutos). */
    historyPruneMs: z.number().int().positive(),
  }),

  capability: z.object({
    /* Budget de frescor do gate material (ADR-0617). Se o snapshot de licença é
       mais velho que isso, o gate força refresh antes de decidir. Trade-off
       UX↔segurança: menor = revogação mais rápida, mais latência no chat.send. */
    freshnessBudgetMs: z.number().int().positive(),
  }),

  ai: z.object({
    quotas: z.object({
      proMonthlyTokens: z.number().int().positive(),
      teamMonthlyTokens: z.number().int().positive(),
    }),
    rateLimits: z.object({
      perMinuteRequests: z.number().int().positive(),
    }),
  }),

  marketplace: z.object({
    revenueSharePublisherPercent: z.number().min(0).max(100),
    curationFastpathThresholdPacks: z.number().int().nonnegative(),
    /* Preço de exibição por pack pago (ADR-0319, §4.17). Chave = packId; fonte
       canônica do preço mostrado no marketplace. O mapping SKU↔pack do checkout
       mora no billing-service (allowlist `PACK_PRICING`), não aqui. Factory
       default (mutável) — nunca `.default({})` compartilhado entre parses. */
    packPricing: z
      .record(
        z.string(),
        z.object({
          priceUsd: moneyAmount,
          interval: z.enum(['month', 'year']).default('month'),
        }),
      )
      .default(() => ({})),
  }),

  capabilities: z.object({
    /* Matriz plano→capability (ADR-0320). Fonte de exibição/edição (admin =
       Epic 28); o licensing deriva o claim do JWT da MESMA matriz. */
    matrix: capabilityMatrixSchema.default(() => ({})),
  }),

  orgs: z.object({
    /* Domínios de email públicos que NÃO podem virar o domínio `verified` de uma org
       (Epic 27 / ADR-0325). Barram o footgun do same-domain auto-join (org com `gmail.com`
       → toda conta @gmail entraria). Admin-curado no config-service (ADR-0551): a lista viva
       vence; default vazio no schema (o seed seguro mora em DEFAULT_CONFIG + no fallback do
       auth-service). Esvaziar a lista (via admin) libera públicos p/ orgs de teste. */
    publicEmailDomains: z.array(z.string().min(1)).default(() => []),
  }),

  accounts: z.object({
    /* Handles (@username) que NÃO podem ser reivindicados por usuários (ADR-0730). Admin-curado
       no config-service — a lista viva vence (§4.17, nunca hardcoded na regra de negócio). O
       auth-service faz UNION com um piso mínimo de segurança (handles estruturais/de rota como
       `admin`/`api`/`root`) que permanece reservado mesmo com o config-service fora do ar (§4.15).
       Default vazio no schema; o seed completo mora em DEFAULT_CONFIG. */
    reservedUsernames: z.array(z.string().min(1)).default(() => []),
  }),

  publisher: z.object({
    /* Constraints da conta de publisher comunitário (TASK-29-02, ADR-0365 §5).
       `slugPattern` é compilado para RegExp em runtime pelo licensing (config
       first-party). Token API: bytes de entropia + teto de tokens ativos. */
    slugPattern: z.string().min(1),
    apiTokenBytes: z.number().int().positive(),
    maxActiveTokensPerPublisher: z.number().int().positive(),
    /* Anti-squatting de namespace (TASK-PUB-04, ADR-0363 §8). `reservedSlugs` = match
       exato normalizado → onboard 409; similar-demais (≥ `squattingSimilarityThreshold`)
       → publisher `pending` p/ review manual. Config-driven (§4.17), nunca hardcoded. */
    reservedSlugs: z.array(z.string().min(1)).default(() => []),
    /* Step-up 2FA na emissão do token de publish (TASK-PUB-04). OTP por e-mail
       (reusa auth p/ entrega); `ttlSeconds` = validade do código; `maxAttempts` =
       lockout. `enabled=false` → gate desligado (não regride emissão). */
    publishStepUp: z.object({
      enabled: z.boolean(),
      ttlSeconds: z.number().int().positive(),
      maxAttempts: z.number().int().positive(),
    }),
    /* Rate-limit persistente (Postgres token-bucket) do onboard + criação de token
       (TASK-PUB-04). Nunca `Map` de módulo (Workers isolate). */
    rateLimits: z.object({
      onboardCapacity: z.number().int().positive(),
      tokenCreateCapacity: z.number().int().positive(),
      refillIntervalMs: z.number().int().positive(),
    }),
    /* Janela (dias) de rotação de chave (TASK-PUB-18, ADR-0469): tempo recomendado que
       a chave nova e a antiga coexistem ativas antes de retirar a antiga. Business value
       (§4.17) — o `retire` bloqueia sem chave ativa alternativa independentemente disto. */
    keyRotationWindowDays: z.number().int().positive(),
    /* Verificação de titularidade de website (TASK-PUB-21, ADR-0472). Challenge por DNS TXT
       e/ou arquivo well-known — sinal de confiança PRÓPRIO (`domainVerified`), desacoplado do
       `status='verified'` manual (ADR-0423). TTL/bytes/paths config-driven (§4.17). */
    domainVerification: z.object({
      ttlSeconds: z.number().int().positive(),
      tokenBytes: z.number().int().positive(),
      txtRecordName: z.string().min(1),
      wellKnownPath: z.string().min(1),
      methods: z.array(z.enum(['dns', 'well-known'])).min(1),
    }),
  }),

  packRegistry: z.object({
    /* Namespace ownership `<prefix>/<pack>` (ADR-0363 §8, ADR-0439). Sem lista de
       prefixos reservados: o namespace é puramente `prefix === publisher.slug`
       (1 publisher = 1 namespace; o primeiro publisher é a própria Planuze).
       `squattingSimilarityThreshold` = levenshtein normalizado (0..1); claim acima
       do limiar vai para review manual. */
    namespacePrefixRegex: z.string().min(1),
    packNameRegex: z.string().min(1),
    squattingSimilarityThreshold: z.number().min(0).max(1),
    /* Rate-limit persistente (KV counter) do publish/submissão por publisher
       (TASK-PUB-04). `capacity` requests por `windowMs`; nunca `Map` de módulo. */
    publishRateLimit: z.object({
      capacity: z.number().int().positive(),
      windowMs: z.number().int().positive(),
    }),
    /* Teto de ciclos `changes_requested` antes do auto-reject da submission
       (TASK-29-05, ADR-0363 §4). Evita loop infinito de revisão. */
    maxChangesIterations: z.number().int().positive(),
    /* Alvo de SLA (horas) da decisão de moderação (ADR-0363 promete 48h; TASK-PUB-12).
       Business value (§4.17): as métricas de SLA (`aboveSlaCount`) e o digest de fila
       pendente medem contra este alvo — nunca hardcodar 48 no service. */
    moderationSlaHours: z.number().int().positive(),
    /* Janela (ms) para reverter um yank de versão (TASK-29-06, ADR-0369). Após
       `yankedAt + unyankWindowMs`, o un-yank é rejeitado (a versão fica yanked
       em definitivo) — evita ressuscitar versão removida há muito tempo. */
    unyankWindowMs: z.number().int().positive(),
    /* Maturidade npm de versões (TASK-PUB-17, ADR-0468). `maxReleaseNotesChars` = teto
       do changelog por versão (bound do doc do índice no KV); `maxDistTagsPerPack` = teto
       de dist-tags distintas por pack (anti-abuso). Business values (§4.17). */
    maxReleaseNotesChars: z.number().int().positive(),
    maxDistTagsPerPack: z.number().int().positive(),
    /* Teto de dependentes (extensions) por template no reverse-index (TASK-PUB-19,
       ADR-0470). Bound anti-abuso do doc `pack:dependents:{parent}` no KV. §4.17. */
    maxDependentsPerTemplate: z.number().int().positive(),
    /* Grace period (ms) para consumidores de uma versão yanked (TASK-29-16, ADR-0380).
       Quem JÁ instalou continua baixando até `yankedAt + yankGraceMs`; depois o
       /download bloqueia (410) — enforcement SERVER-SIDE (não confia no cliente). */
    yankGraceMs: z.number().int().positive(),
    /* Kill-switch do fastpath de auto-aprovação (TASK-29-17, ADR-0374). Quando um
       publisher `verified` publica e o scan dá `pass`, a submission pula a review
       humana (reviewing→approved automático). `false` = todo pack vai para review
       manual (circuit-breaker global de ops em incidente). */
    fastpathEnabled: z.boolean(),
    /* Política do scanner de segurança (TASK-29-07, ADR-0370). O runner injeta as
       severidades em `ScanOptions`; falha de parse é a exceção fail-closed e sempre
       permanece como erro habilitado. `scanTimeoutMs` = backstop p/ submission presa. */
    scanner: scannerPolicySchema.transform(enforceRequiredScannerPolicy),
    /* Gates automáticos de promoção pública (ADR-0602). `scanFailOn` = quais status do
       scanner REPROVAM o gate de scan (produto decide se `warn` bloqueia); `runnerTimeoutMs`
       = timeout do push ao runner de scan (decifra+AST em memória); `liveBlobTtlSec` = TTL da
       URL HMAC do blob live entregue ao runner; `callbackTimeoutMs` limita chamadas de confirmação
       registry→licensing; `statusPollIntervalMs` mantém a UI sincronizada enquanto existe uma
       promoção viva. §4.17 — nunca hardcoded no orquestrador. */
    promotionGates: z.object({
      scanFailOn: z.array(z.enum(['fail', 'warn'])),
      runnerTimeoutMs: z.number().int().positive(),
      liveBlobTtlSec: z.number().int().positive(),
      callbackTimeoutMs: z.number().int().positive(),
      statusPollIntervalMs: z.number().int().positive(),
    }),
    /* Analytics de publisher (TASK-29-13, ADR-0363). `maxRangeMs` = janela máxima de
       consulta (anti-abuso); `regionMinEvents` = mínimo de eventos com região p/ expor
       o breakdown (privacidade — evita re-identificação); `topPacksLimit` = teto da
       lista de packs. Valores de produto (§4.17), nunca hardcoded no service. */
    analytics: z.object({
      maxRangeMs: z.number().int().positive(),
      regionMinEvents: z.number().int().positive(),
      topPacksLimit: z.number().int().positive(),
    }),
    /* Busca do marketplace (TASK-29-08, ADR-0382). Limites de paginação (anti-abuso —
       page size grande = scan/serialização cara). §4.17: nunca hardcoded no service. */
    search: z.object({
      maxPageSize: z.number().int().positive(),
      defaultPageSize: z.number().int().positive(),
    }),
  }),
});

export type RemoteConfig = z.infer<typeof RemoteConfigSchema>;

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
/* Cadência default da varredura do prune de histórico (ADR-0604): 6h. Varredura
   barata — a retenção efetiva (dias) é a barreira de negócio, não o intervalo. */
const HISTORY_PRUNE_HOURS = 6;
const REFRESH_LICENSE_MINUTES = 60;
const REFRESH_CONFIG_MINUTES = 30;
const WATCHER_DEBOUNCE_MS = 200;
const AUTOSAVE_MS = 30_000;
/* Frescor do gate material (ADR-0617): metade do TTL do access token (≈1h). A
   revogação server-side é ≤1min (ADR-0452/0519); 30min é o teto de latência
   aceitável no app antes de forçar refresh numa operação que gasta ai.tokens. */
const FRESHNESS_BUDGET_MS = 30 * MS_PER_MINUTE;
/* 7 dias em ms — janela default de reversão de yank (ADR-0369). */
const UNYANK_WINDOW_MS = 604_800_000;
const YANK_GRACE_MS = 30 * 24 * 60 * 60 * 1000;
/* Maturidade npm de versões (ADR-0468): teto do changelog por versão + teto de dist-tags. */
const MAX_RELEASE_NOTES_CHARS = 4_000;
const MAX_DIST_TAGS_PER_PACK = 20;
/* Teto de dependentes por template no reverse-index (ADR-0470). */
const MAX_DEPENDENTS_PER_TEMPLATE = 500;
/* Scanner (ADR-0370): teto de findings, timeout do scan e backstop p/ presos. */
const SCANNER_MAX_FINDINGS = 100;
const SCANNER_TIMEOUT_MS = 300_000;
const SCANNER_STUCK_TIMEOUT_MS = 900_000;
/* Escopo ampliado (ADR-0450 fatia 02a): base64 contíguo ≥512 chars em dados não-código =
   payload suspeito; >20 arquivos vendados escala bundled-deps para error. */
const SCANNER_DATA_MIN_BASE64 = 512;
const SCANNER_MAX_VENDORED_FILES = 20;
/* Gates de promoção pública (ADR-0602): `warn` anota mas não bloqueia (OPEN de produto);
   scan decifra+AST em memória → timeout folgado; janela curta da URL HMAC do blob live. */
const PROMOTION_GATE_SCAN_FAIL_ON = ['fail'] as const;
const PROMOTION_GATE_RUNNER_TIMEOUT_MS = 60_000;
const PROMOTION_GATE_LIVE_BLOB_TTL_SEC = 300;
const PROMOTION_GATE_CALLBACK_TIMEOUT_MS = 8_000;
const PROMOTION_STATUS_POLL_INTERVAL_MS = 5_000;
/* Analytics (ADR-0363, TASK-29-13): janela máx. de consulta (1 ano), mínimo de eventos
   p/ expor região (privacidade) e teto de top packs. */
const ANALYTICS_MAX_RANGE_MS = 365 * 24 * 60 * 60 * 1000;
const SEARCH_MAX_PAGE_SIZE = 50;
const SEARCH_DEFAULT_PAGE_SIZE = 20;
const ANALYTICS_REGION_MIN_EVENTS = 50;
const ANALYTICS_TOP_PACKS_LIMIT = 10;
/* Publisher hardening (ADR TASK-PUB-04). Step-up: código OTP vale 10min, 5 tentativas.
   Rate-limits: onboard 5/min, criação de token 10/min, publish 20/min. Slugs de marca
   reservados (config-driven — o publisher #1 é a própria Planuze). */
const STEP_UP_TTL_SECONDS = 600;
const STEP_UP_MAX_ATTEMPTS = 5;
/* Janela recomendada de rotação de chave do publisher (ADR-0469): 30 dias de overlap. */
const KEY_ROTATION_WINDOW_DAYS = 30;
/* Verificação de titularidade de website (ADR-0472): challenge DNS TXT / well-known. TTL de
   1h p/ o publisher configurar o registro; token de 16 bytes (128 bits) hex. */
const DOMAIN_CHALLENGE_TTL_SECONDS = 3600;
const DOMAIN_CHALLENGE_TOKEN_BYTES = 16;
const ONBOARD_RL_CAPACITY = 5;
/* Anti-flood da criação de token: folgado ACIMA do teto de tokens ativos
   (`maxActiveTokensPerPublisher`) — o teto de negócio é a barreira, o rate-limit só
   corta burst automatizado. */
const TOKEN_CREATE_RL_CAPACITY = 30;
const PUBLISH_RL_CAPACITY = 20;
/* Alvo de SLA de decisão de moderação (ADR-0363: 48h). */
const MODERATION_SLA_HOURS = 48;
const RESERVED_SLUGS = [
  'planuze',
  'planuze-official',
  'official',
  'admin',
  'stripe',
  'react',
  'vue',
  'angular',
  'aws',
  'gcp',
  'azure',
  'google',
  'microsoft',
  'community',
];
/* Seed dos @username reservados (ADR-0730). Curado pelo admin depois; o auth-service
   ainda une isto com um piso de segurança estrutural. */
const RESERVED_USERNAMES = [
  'admin',
  'root',
  'system',
  'staff',
  'support',
  'help',
  'planuze',
  'api',
  'www',
  'me',
  'account',
  'billing',
  'org',
  'orgs',
  'user',
  'users',
  'null',
  'undefined',
];

/** Defaults usados quando config-service indisponível na primeira execução. */
export const DEFAULT_CONFIG: RemoteConfig = {
  configVersion: 1,
  generatedAt: 0,
  pricing: {
    pro: { monthlyBrl: 39, annualBrl: 349, currency: 'BRL' },
    team: { monthlyBrl: 99 },
    perTemplate: { monthlyBrl: 19, annualBrl: 169 },
  },
  refund: { annualDays: 14, monthlyDays: 0, oneTimeDays: 14 },
  trial: { premiumPackDays: 14, requiresPaymentMethod: false },
  limits: {
    recentProjects: 15,
    packSizeMb: 50,
    historyDays: 30,
    chatSessionsPerProject: 50,
  },
  retention: {
    logsDays: 7,
    backupsDays: 30,
    debugBundleMaxMb: 10,
    deviceHeartbeatsDays: 30,
  },
  intervals: {
    licenseRefreshMs: REFRESH_LICENSE_MINUTES * MS_PER_MINUTE,
    configRefreshMs: REFRESH_CONFIG_MINUTES * MS_PER_MINUTE,
    fileWatcherDebounceMs: WATCHER_DEBOUNCE_MS,
    autoSaveMs: AUTOSAVE_MS,
    historyPruneMs: HISTORY_PRUNE_HOURS * MS_PER_HOUR,
  },
  capability: { freshnessBudgetMs: FRESHNESS_BUDGET_MS },
  ai: {
    quotas: { proMonthlyTokens: 50_000, teamMonthlyTokens: 200_000 },
    rateLimits: { perMinuteRequests: 30 },
  },
  marketplace: {
    revenueSharePublisherPercent: 70,
    curationFastpathThresholdPacks: 3,
    packPricing: {},
  },
  capabilities: { matrix: DEFAULT_CAPABILITY_MATRIX },
  /* Domínios de email públicos barrados como domínio de org: FONTE ÚNICA = lista curada pelo admin
     no config-service (ADR-0325/0551 revistos). SEM seed hardcoded — vazio até o admin cadastrar
     (decisão do owner: nada de default gmail). */
  orgs: { publicEmailDomains: [] },
  accounts: { reservedUsernames: RESERVED_USERNAMES },
  publisher: {
    slugPattern: '^[a-z0-9](?:[a-z0-9-]{0,37}[a-z0-9])?$',
    apiTokenBytes: 32,
    maxActiveTokensPerPublisher: 10,
    reservedSlugs: RESERVED_SLUGS,
    publishStepUp: {
      enabled: true,
      ttlSeconds: STEP_UP_TTL_SECONDS,
      maxAttempts: STEP_UP_MAX_ATTEMPTS,
    },
    keyRotationWindowDays: KEY_ROTATION_WINDOW_DAYS,
    rateLimits: {
      onboardCapacity: ONBOARD_RL_CAPACITY,
      tokenCreateCapacity: TOKEN_CREATE_RL_CAPACITY,
      refillIntervalMs: MS_PER_MINUTE,
    },
    domainVerification: {
      ttlSeconds: DOMAIN_CHALLENGE_TTL_SECONDS,
      tokenBytes: DOMAIN_CHALLENGE_TOKEN_BYTES,
      txtRecordName: '_planuze-verify',
      wellKnownPath: '/.well-known/planuze-publisher-verification.txt',
      methods: ['dns', 'well-known'],
    },
  },
  packRegistry: {
    namespacePrefixRegex: '^[a-z][a-z0-9-]{1,38}$',
    packNameRegex: '^[a-z0-9][a-z0-9._-]{0,62}$',
    squattingSimilarityThreshold: 0.85,
    publishRateLimit: { capacity: PUBLISH_RL_CAPACITY, windowMs: MS_PER_MINUTE },
    maxChangesIterations: 3,
    moderationSlaHours: MODERATION_SLA_HOURS,
    unyankWindowMs: UNYANK_WINDOW_MS,
    maxReleaseNotesChars: MAX_RELEASE_NOTES_CHARS,
    maxDistTagsPerPack: MAX_DIST_TAGS_PER_PACK,
    maxDependentsPerTemplate: MAX_DEPENDENTS_PER_TEMPLATE,
    yankGraceMs: YANK_GRACE_MS,
    fastpathEnabled: true,
    scanner: {
      enabled: true,
      maxFindings: SCANNER_MAX_FINDINGS,
      maxArchiveBytes: 32 * 1024 * 1024,
      timeoutMs: SCANNER_TIMEOUT_MS,
      scanTimeoutMs: SCANNER_STUCK_TIMEOUT_MS,
      dataPayloadMinBase64: SCANNER_DATA_MIN_BASE64,
      maxVendoredFiles: SCANNER_MAX_VENDORED_FILES,
      failOn: ['error'],
      rules: [
        { id: 'eval-call', severity: 'error', enabled: true },
        { id: 'new-function', severity: 'error', enabled: true },
        { id: 'dynamic-code-reference', severity: 'error', enabled: true },
        { id: 'process-builtin-loader', severity: 'error', enabled: true },
        { id: 'process-native-escape', severity: 'error', enabled: true },
        { id: 'vm-code-loading', severity: 'error', enabled: true },
        { id: 'child-process-import', severity: 'error', enabled: true },
        { id: 'dynamic-require', severity: 'error', enabled: true },
        { id: 'dynamic-import', severity: 'error', enabled: true },
        { id: 'fs-write-arbitrary', severity: 'error', enabled: true },
        { id: 'net-connect-unallowed', severity: 'error', enabled: true },
        { id: 'process-env-secrets', severity: 'warning', enabled: true },
        { id: 'crypto-weak-random', severity: 'info', enabled: true },
        { id: 'obfuscation-suspect', severity: 'error', enabled: true },
        { id: 'data-embedded-payload', severity: 'warning', enabled: true },
        { id: 'bundled-deps-present', severity: 'warning', enabled: true },
        { id: 'dataflow-secret-exfiltration', severity: 'warning', enabled: true },
      ],
    },
    promotionGates: {
      scanFailOn: [...PROMOTION_GATE_SCAN_FAIL_ON],
      runnerTimeoutMs: PROMOTION_GATE_RUNNER_TIMEOUT_MS,
      liveBlobTtlSec: PROMOTION_GATE_LIVE_BLOB_TTL_SEC,
      callbackTimeoutMs: PROMOTION_GATE_CALLBACK_TIMEOUT_MS,
      statusPollIntervalMs: PROMOTION_STATUS_POLL_INTERVAL_MS,
    },
    analytics: {
      maxRangeMs: ANALYTICS_MAX_RANGE_MS,
      regionMinEvents: ANALYTICS_REGION_MIN_EVENTS,
      topPacksLimit: ANALYTICS_TOP_PACKS_LIMIT,
    },
    search: {
      maxPageSize: SEARCH_MAX_PAGE_SIZE,
      defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
    },
  },
};
